import { useState, useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { tokenUtils } from '../utils/tokenUtils';

const useNotifications = (
  apiEndpoint, 
  hubUrl = `${process.env.REACT_APP_LOCATION}/notificationHub`,
  baseUrl = `${process.env.REACT_APP_LOCATION}`
) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [toastNotification, setToastNotification] = useState(null);
  
  const connectionRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const connectionAttemptsRef = useRef(0);

  function getAuthHeaders() {
    const token = tokenUtils.get();
    if (!token) throw new Error('No se encontró token de autenticación');
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  const cleanNotificationData = (notification) => {
    return {
      ...notification,
      id: notification.id || notification.notificationId,
      title: notification.title?.trim() || '',
      message: notification.message?.trim() || '',
      courseName: notification.courseName?.trim() || null,
      isRead: notification.isRead || false,
      createdAt: notification.createdAt ? new Date(notification.createdAt).toISOString() : new Date().toISOString(),
      timestamp: notification.createdAt ? new Date(notification.createdAt).getTime() : Date.now()
    };
  };

  // Función para obtener notificaciones iniciales
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const fullEndpoint = apiEndpoint.startsWith('http') ? apiEndpoint : `${baseUrl}${apiEndpoint}`;
      const response = await fetch(fullEndpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      const cleanedNotifications = data
        .map(cleanNotificationData)
        .sort((a, b) => b.timestamp - a.timestamp);
      
      setNotifications(cleanedNotifications);
      setUnreadCount(cleanedNotifications.filter(notification => !notification.isRead).length);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('❌ Error fetching notifications:', {
        message: err.message,
        stack: err.stack,
        endpoint: apiEndpoint,
        baseUrl: baseUrl
      });
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, baseUrl]);

  // Función para mostrar notificaciones del navegador
  const showBrowserNotification = useCallback((notification) => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        const browserNotification = new Notification(notification.title, {
          body: notification.message,
          icon: '/img/logocom.png',
          tag: `notification-${notification.id}`,
          requireInteraction: false,
          silent: false,
        });

        browserNotification.onclick = () => {
          window.focus();
          markAsRead(notification.id);
          browserNotification.close();
        };

        setTimeout(() => {
          browserNotification.close();
        }, 5000);
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            showBrowserNotification(notification);
          }
        });
      }
    }
  }, []);

  // Función para marcar una notificación como leída
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const fullEndpoint = apiEndpoint.startsWith('http') ? apiEndpoint : `${baseUrl}${apiEndpoint}`;
      const response = await fetch(`${fullEndpoint}/mark-read/${notificationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: No se pudo marcar como leída`);
      }

      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true, readAt: new Date().toISOString() }
            : notification
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));

      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        try {
          await connectionRef.current.invoke('MarkAsRead', notificationId);
        } catch (signalRError) {
          console.error('❌ Error notifying SignalR hub:', {
            message: signalRError.message,
            stack: signalRError.stack,
            notificationId: notificationId
          });
        }
      }
    } catch (err) {
      setError(`Error al marcar como leída: ${err.message}`);
      console.error('❌ Error marking notification as read:', {
        message: err.message,
        stack: err.stack,
        notificationId: notificationId
      });
    }
  }, [apiEndpoint, baseUrl]);

  // Función para marcar todas las notificaciones como leídas
  const markAllAsRead = useCallback(async () => {
    try {
      const fullEndpoint = apiEndpoint.startsWith('http') ? apiEndpoint : `${baseUrl}${apiEndpoint}`;
      const response = await fetch(`${fullEndpoint}/mark-all-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: No se pudieron marcar todas como leídas`);
      }

      const readAt = new Date().toISOString();
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true, readAt }))
      );
      setUnreadCount(0);

      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        try {
          await connectionRef.current.invoke('MarkAllAsRead');
        } catch (signalRError) {
          console.error('❌ Error notifying SignalR hub:', {
            message: signalRError.message,
            stack: signalRError.stack
          });
        }
      }
    } catch (err) {
      setError(`Error al marcar todas como leídas: ${err.message}`);
      console.error('❌ Error marking all notifications as read:', {
        message: err.message,
        stack: err.stack
      });
    }
  }, [apiEndpoint, baseUrl]);

  // Función para eliminar una notificación
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      const fullEndpoint = apiEndpoint.startsWith('http') ? apiEndpoint : `${baseUrl}${apiEndpoint}`;
      const response = await fetch(`${fullEndpoint}/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: No se pudo eliminar la notificación`);
      }

      setNotifications(prev => {
        const filtered = prev.filter(notification => notification.id !== notificationId);
        const deletedNotification = prev.find(n => n.id === notificationId);
        
        if (deletedNotification && !deletedNotification.isRead) {
          setUnreadCount(current => Math.max(0, current - 1));
        }
        
        return filtered;
      });

    } catch (err) {
      setError(`Error al eliminar notificación: ${err.message}`);
      console.error('❌ Error deleting notification:', {
        message: err.message,
        stack: err.stack,
        notificationId: notificationId
      });
    }
  }, [apiEndpoint, baseUrl]);

  // Función para conectar a SignalR con manejo de errores mejorado
    const connectToSignalR = useCallback(async () => {
    try {
      const token = tokenUtils.get();
      if (!token) {
        const error = new Error('No token available for SignalR connection');
        console.error('❌ SignalR Connection Error:', {
          message: error.message,
          type: 'AUTHENTICATION_ERROR',
          timestamp: new Date().toISOString()
        });
        throw error;
      }

      let fullHubUrl = `${hubUrl}`;
      fullHubUrl = fullHubUrl.replace(/([^:]\/)\/+/g, '$1');
      
      console.log('🔗 Intentando conectar a SignalR:', {
        url: fullHubUrl,
        attempt: connectionAttemptsRef.current + 1,
        timestamp: new Date().toISOString()
      });

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(fullHubUrl, {
          accessTokenFactory: () => token,
          withCredentials: true 
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: retryContext => {
            console.log('🔄 SignalR Reconnection attempt:', {
              attempt: retryContext.previousRetryCount + 1,
              elapsedMilliseconds: retryContext.elapsedMilliseconds,
              retryReason: retryContext.retryReason
            });
            
            if (retryContext.elapsedMilliseconds < 60000) {
              return Math.min(2000 + Math.random() * 2000, 10000);
            }
            return Math.min(30000 + Math.random() * 10000, 60000);
          }
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Configurar event handlers con logging detallado
      connection.on("ReceiveNotification", (notification) => {
        console.log('📨 SignalR Notification Received:', {
          notificationId: notification.id,
          title: notification.title,
          timestamp: new Date().toISOString()
        });
        
        const cleanedNotification = cleanNotificationData(notification);

        setToastNotification(cleanedNotification);
        fetchNotifications();
        
        if (toastTimeoutRef.current) {
          clearTimeout(toastTimeoutRef.current);
        }

        toastTimeoutRef.current = setTimeout(() => {
          setToastNotification(null);
        }, 8000);

        showBrowserNotification(cleanedNotification);
      });

      connection.on("NotificationRead", (notificationId) => {
        console.log('✅ SignalR Notification Read:', {
          notificationId: notificationId,
          timestamp: new Date().toISOString()
        });
        
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, isRead: true, readAt: new Date().toISOString() }
              : notification
          )
        );
        
        setUnreadCount(prev => Math.max(0, prev - 1));
      });

      connection.on("AllNotificationsRead", () => {
        console.log('✅ SignalR All Notifications Read:', {
          timestamp: new Date().toISOString()
        });
        
        const readAt = new Date().toISOString();
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, isRead: true, readAt }))
        );
        setUnreadCount(0);
      });

      connection.on("NotificationDeleted", (notificationId) => {
        console.log('🗑️ SignalR Notification Deleted:', {
          notificationId: notificationId,
          timestamp: new Date().toISOString()
        });
        
        setNotifications(prev => {
          const filtered = prev.filter(notification => notification.id !== notificationId);
          const deletedNotification = prev.find(n => n.id === notificationId);
          
          if (deletedNotification && !deletedNotification.isRead) {
            setUnreadCount(current => Math.max(0, current - 1));
          }
          
          return filtered;
        });
      });

      connection.onreconnecting(error => {
        console.warn('🔄 SignalR Reconnecting:', {
          error: error?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        setIsConnected(false);
      });

      connection.onreconnected(connectionId => {
        console.log('✅ SignalR Reconnected:', {
          connectionId: connectionId,
          timestamp: new Date().toISOString()
        });
        setIsConnected(true);
        connectionAttemptsRef.current = 0; // Reset attempts on successful connection
        fetchNotifications();
      });

      connection.onclose(error => {
        console.warn('❌ SignalR Connection Closed:', {
          error: error?.message || 'No error',
          timestamp: new Date().toISOString()
        });
        setIsConnected(false);
        
        if (error && reconnectTimeoutRef.current === null) {
          console.log('⏰ Scheduling SignalR reconnection...');
          reconnectTimeoutRef.current = setTimeout(() => {
            connectToSignalR();
            reconnectTimeoutRef.current = null;
          }, 5000);
        }
      });

      // Intentar conectar con timeout
      const connectionPromise = connection.start();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Connection timeout after 30 seconds')), 30000);
      });

      await Promise.race([connectionPromise, timeoutPromise]);
      
      console.log('✅ SignalR Connection Established:', {
        connectionId: connection.connectionId,
        timestamp: new Date().toISOString()
      });
      
      // Subscribe to user-specific notifications
      try {
        const userId = JSON.parse(localStorage.getItem("usuarioConCedula") || "{}").id;
        if (userId) {
          const userIdString = userId.toString();
          await connection.invoke("JoinUserGroup", userIdString);
          console.log('✅ SignalR User Group Joined:', {
            userId: userIdString,
            timestamp: new Date().toISOString()
          });
        } else {
          console.warn('⚠️ No se pudo obtener el ID del usuario para la suscripción');
        }
      } catch (subscriptionError) {
        console.error('❌ Error al suscribirse a notificaciones:', {
          message: subscriptionError.message,
          stack: subscriptionError.stack,
          timestamp: new Date().toISOString()
        });
      }
      
      connectionRef.current = connection;
      setError(null);
      setIsConnected(true);
      connectionAttemptsRef.current = 0; // Reset attempts on successful connection

    } catch (err) {
      connectionAttemptsRef.current += 1;
      
      console.error('❌ SignalR Connection Error:', {
        message: err.message,
        stack: err.stack,
        name: err.name,
        attempt: connectionAttemptsRef.current,
        hubUrl: hubUrl,
        timestamp: new Date().toISOString(),
        errorType: err.name,
        errorDetails: {
          isNetworkError: err.message.includes('Failed to fetch'),
          isTimeoutError: err.message.includes('timeout'),
          isAuthError: err.message.includes('Unauthorized') || err.message.includes('401'),
          isCorsError: err.message.includes('CORS') || err.message.includes('Access-Control'),
          isNegotiationError: err.message.includes('negotiation')
        }
      });
      
      setError(`Error de conexión SignalR: ${err.message}`);
      setIsConnected(false);
      
      if (reconnectTimeoutRef.current === null) {
        const retryDelay = Math.min(5000 * connectionAttemptsRef.current, 30000);
        console.log(`⏰ Reintentando conexión SignalR en ${retryDelay}ms (intento ${connectionAttemptsRef.current})`);
        reconnectTimeoutRef.current = setTimeout(() => {
          connectToSignalR();
          reconnectTimeoutRef.current = null;
        }, retryDelay);
      }
    }
  }, [hubUrl, baseUrl, fetchNotifications, showBrowserNotification]);
  
  // Función para desconectar manualmente
  const disconnect = useCallback(async () => {
    if (connectionRef.current) {
      try {
        console.log('🔌 Disconnecting SignalR manually...');
        await connectionRef.current.stop();
        connectionRef.current = null;
        setIsConnected(false);
        console.log('✅ SignalR disconnected successfully');
      } catch (err) {
        console.error('❌ Error disconnecting from SignalR:', {
          message: err.message,
          stack: err.stack
        });
      }
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Función para reconectar manualmente
  const reconnect = useCallback(async () => {
    console.log('🔄 Manual SignalR reconnection initiated...');
    await disconnect();
    await connectToSignalR();
  }, [disconnect, connectToSignalR]);

  // Configurar conexión inicial y permisos de notificaciones
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    fetchNotifications();
    connectToSignalR();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (connectionRef.current) {
        connectionRef.current.stop().catch(err => {
          console.error('❌ Error stopping SignalR connection:', {
            message: err.message,
            stack: err.stack
          });
        });
        connectionRef.current = null;
      }
    };
  }, [fetchNotifications, connectToSignalR]);

  // Funciones utilitarias
  const getNotificationsByType = useCallback((type) => {
    return notifications.filter(notification => notification.type === type);
  }, [notifications]);

  const getUnreadNotifications = useCallback(() => {
    return notifications.filter(notification => !notification.isRead);
  }, [notifications]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    isConnected,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch: fetchNotifications,
    disconnect,
    reconnect,
    getNotificationsByType,
    getUnreadNotifications,
    clearError,
    toastNotification,
    dismissToast: () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
      setToastNotification(null);
    }
  };
};

export default useNotifications;